import React, { useState, useCallback, useRef, useEffect } from "react";
import { message } from "antd";
import { toast, Id } from "react-toastify";
import mammoth from 'mammoth';
import IcWarning from "@/assets/svg/ic_warning.svg?react";
import IcBlueArrow from "@/assets/simulInter/ic_blue_arrow.svg?react";
import FilePreview from "./components/FilePreview";
import Popover from "@/component/Popover";
import IC_RIGHT_ARROW from '@/assets/simulInter/ic_right_arrow.svg?react'
import IC_MORE from '@/assets/simulInter/ic_more.svg?react'
import IC_FOLD from '@/assets/simulInter/ic_fold.svg?react'
import AutoTransEnhanceTips, { AutoTransEnhanceTipsRef } from "./components/AutoTransEnhanceTips";
import TransEnhanceModal, { TransEnhanceModalRef } from "./components/TransEnhanceModal";
import FileUploader, { FileUploaderRef } from "./components/FileUploader";
import uploadFileToCstore, { selectFile } from "@/utils/uploadHelper";
import { deleteHotWords, deleteSpeechDraft, generateHotWords, getSpeechDraft, saveSpeechDraft, updateHotWords } from "@/services/simulInter";
import downloadFileFromUrl from "@/utils/downloadHelper";
export interface UploadedFile {
  file: File;
  url: string;
  type: "pdf" | "docx" | "doc" | "txt";
  name: string;
  size: number;
}

let hotWordsUpdateFailedToastId: Id;

const SpeechDraftView: React.FC<{ visible: boolean, onToggleSpeechDraft: () => void, showDraftView: boolean, streamName: string, isFromList: boolean }> = ({ visible, onToggleSpeechDraft, showDraftView, streamName, isFromList }) => {
  const [loading, setLoading] = useState(true);
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showMoreList, setShowMoreList] = useState(false);
  const [speechDraftUrl, setSpeechDraftUrl] = useState<string>(''); // 保存发言稿的 COS URL
  const autoTransEnhanceTipsRef = useRef<AutoTransEnhanceTipsRef>(null);
  const transEnhanceModalRef = useRef<TransEnhanceModalRef>(null);
  const fileUploaderRef = useRef<FileUploaderRef>(null);
  const firstUploadRef = useRef(true);
  const newHotWordsRef = useRef<string[]>([]);
  const uploadedFileRef = useRef<UploadedFile | null>(null);
  const generateHotWordsAbortControllerRef = useRef<AbortController | null>(null);

  // 页面加载判断是否有已上传的发言稿，有的话则直接加载
  // 只有从列表进入时才需要加载，控制加入进入时不需要（肯定没有发言稿）
  useEffect(() => {
    const loadUploadedFile = async () => {
      try {
        setLoading(true);

        const res = await getSpeechDraft(streamName, '发言稿.docx');
        if (!res.data) {
          return;
        }
        const url = res.data;
        setSpeechDraftUrl(url);
        console.log('开始下载文件:', url);

        // 下载文件
        const file = await downloadFile(url);
        if (file) {
          setUploadedFile(file);
          uploadedFileRef.current = file;
          firstUploadRef.current = false;
          console.log('文件加载完成:', file.name);
        }
      } catch (error) {
        console.error('加载文件失败:', error);
        message.error('文件加载失败，请重试');
      } finally {
        setLoading(false);
      }
    };

    // 只有从列表进入且有 streamName 时才加载发言稿
    if (streamName && isFromList) {
      loadUploadedFile();
    } else {
      // 控制加入进入时直接设置为不加载
      setLoading(false);
    }
  }, [streamName, isFromList]);

  const downloadFile = async (url: string) => {
    try {
      const file = await downloadFileFromUrl(url);
      return file;

    } catch (error) {
      console.error('文件下载失败:', error);
      message.error(`文件下载失败: ${error instanceof Error ? error.message : '未知错误'}`);
      return null;
    }
  };

  const uploadFile = async (file: File) => {
    console.log('开始上传文件');
    try {
      const res = await uploadFileToCstore(file);
      console.log('uploadToCstore res', res);
      return res;
    } catch (error) {
      console.log("上传失败:", error);
      message.warning("上传失败，请重试");
      return null;
    }
  };

  /**
   * 提取文件的第一段话（前100字符）
   */
  const extractFirstParagraph = async (file: File): Promise<string> => {
    try {
      const extension = file.name.split(".").pop()?.toLowerCase();
      let text = '';

      switch (extension) {
        case "txt":
          // 读取纯文本文件
          text = await file.text();
          break;

        case "docx":
        case "doc":
          // 使用 mammoth 解析 Word 文档
          const arrayBuffer = await file.arrayBuffer();
          const result = await mammoth.extractRawText({ arrayBuffer });
          text = result.value;
          break;

        default:
          console.warn(`不支持提取 ${extension} 类型文件的文本`);
          return '';
      }

      // 清理文本：去除多余空格和换行
      text = text.trim().replace(/\s+/g, ' ');

      // 提取第一段（前100个字符）
      const firstParagraph = text.substring(0, 100);
      console.log('提取的第一段话:', firstParagraph);

      return firstParagraph;
    } catch (error) {
      console.error('提取文件第一段话失败:', error);
      return '';
    }
  };

  const handleFileUpload = useCallback(async (file: File) => {
    setIsUploading(true);

    // 创建文件URL用于预览
    const url = URL.createObjectURL(file);

    // 确定文件类型
    const extension = file.name.split(".").pop()?.toLowerCase();
    let type: UploadedFile["type"];

    switch (extension) {
      case "pdf":
        type = "pdf";
        break;
      case "docx":
        type = "docx";
        break;
      case "doc":
        type = "doc";
        break;
      case "txt":
        type = "txt";
        break;
      default:
        message.warning("不支持的文件类型");
        setIsUploading(false);
        return;
    }

    const uploadedFileData: UploadedFile = {
      file,
      url,
      type,
      name: file.name,
      size: file.size,
    };

    setUploadedFile(uploadedFileData);
    uploadedFileRef.current = uploadedFileData;

    // 取消正在进行的热词提取请求
    if (generateHotWordsAbortControllerRef.current) {
      console.log('取消正在进行的热词提取请求');
      generateHotWordsAbortControllerRef.current.abort();
      generateHotWordsAbortControllerRef.current = null;
    }
    const res = await uploadFile(file);
    if (res?.downloadUrl) {
      // 保存 COS URL，用于重新提取热词
      setSpeechDraftUrl(res.downloadUrl);
      autoTransEnhanceTipsRef.current?.handleHideTips();
      setIsUploading(false);

      try {
        // 创建新的 AbortController
        const abortController = new AbortController();
        generateHotWordsAbortControllerRef.current = abortController;

        // 提取文件第一段话
        const firstParagraph = await extractFirstParagraph(file);

        await saveSpeechDraft({
          originStreamName: streamName,
          reportFileKey: res.fileKey,
          controllerStatus: 2,
          reportFileName: '发言稿.docx',
          reportFirstSentence: firstParagraph
        }, { signal: abortController.signal });

        const hotWordsRes = await generateHotWords(
          { streamName, url: res.downloadUrl },
          { signal: abortController.signal }
        );
        const words = hotWordsRes.data;

        // 检查文件是否已被删除
        if (!uploadedFileRef.current) {
          console.log('文件已被删除，跳过热词提示');
          return;
        }

        if (words.length === 0) {
          console.log('未提取到热词，跳过热词提示');
          return;
        }

        // 如果是第一次上传，则出现【发现热词】的提示
        console.log('firstUploadRef.current', firstUploadRef.current);
        if (firstUploadRef.current) {
          await updateHotWords({ streamName, words });
          autoTransEnhanceTipsRef.current?.startPollingHotWords(streamName);
        } else {
          // 如果是之后上传，则出现提取到新热词提醒
          newHotWordsRef.current = words;
          autoTransEnhanceTipsRef.current?.coverHotWords(false, words);
        }

      } catch (error) {
        // 如果是取消请求，不显示错误
        if (error && typeof error === 'object' && 'errCode' in error && error.errCode === 'ERR_CANCELED') {
          console.log("提取热词请求已取消");
        } else {
          console.error("提取热词失败：", error);
        }
      } finally {
        // 清理 AbortController
        generateHotWordsAbortControllerRef.current = null;
      }
    } else {
      setIsUploading(false);
    }

  }, [streamName]);

  const handleFileRemove = async (relateToDelete: boolean = true) => {
    // 正在上传时，取消上传
    if (isUploading) {
      message.warning("正在上传中，请稍后再试");
      return;
    }
    // 取消正在进行的热词提取请求
    if (generateHotWordsAbortControllerRef.current) {
      console.log('取消正在进行的热词提取请求');
      generateHotWordsAbortControllerRef.current.abort();
      generateHotWordsAbortControllerRef.current = null;
    }

    if (uploadedFile?.url) {
      URL.revokeObjectURL(uploadedFile.url);
    }
    setUploadedFile(null);
    uploadedFileRef.current = null;
    setShowMoreList(false);
    if (relateToDelete) {
      await deleteSpeechDraft(streamName);
      deleteHotWords(streamName);
    }
  };

  // 组件卸载时清理Blob URL
  useEffect(() => {
    return () => {
      if (uploadedFile?.url) {
        URL.revokeObjectURL(uploadedFile.url);
      }
      // 清理热词更新失败提示
      hotWordsUpdateFailedToastId && toast.dismiss(hotWordsUpdateFailedToastId);
    };
  }, [uploadedFile?.url]);

  const handleShowTransEnhanceModal = () => {
    transEnhanceModalRef.current?.open();
    setShowMoreList(false);
  };

  const handleReUpload = async () => {
    // 正在上传时不允许重新上传
    if (isUploading) {
      message.warning("正在上传中，请稍后再试");
      return;
    }

    // 使用统一的文件选择器
    selectFile({
      accept: ".docx",
      maxSize: 20 * 1024 * 1024, // 20MB
      allowedExtensions: ["docx"],
      allowedMimeTypes: ["application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
      onSelect: async (file) => {
        // 验证通过，先删除旧文件
        await handleFileRemove(false);
        firstUploadRef.current = false;
        // 再上传新文件
        handleFileUpload(file);
      },
      onError: (error) => {
        message.warning(error);
      }
    });
  };

  // 热词更新失败提示组件
  const HotWordsUpdateFailedContent = React.memo(() => {

    const handleOpenSettings = () => {
      hotWordsUpdateFailedToastId && toast.dismiss(hotWordsUpdateFailedToastId);
      transEnhanceModalRef.current?.open();
    };

    return (
      <div className="flex items-center">
        <div>
          转写热词更新失败，请手动更新
          <span
            className="text-#5698FF cursor-pointer ml-1"
            onClick={handleOpenSettings}
          >
            前往设置
          </span>
        </div>
        <IcBlueArrow className="w-3 h-3 mt-0.5 cursor-pointer" onClick={handleOpenSettings} />
      </div>
    );
  });

  const handleCover = async () => {
    autoTransEnhanceTipsRef.current?.handleHideTips();
    try {
      await updateHotWords({ streamName, words: newHotWordsRef.current });
      message.success("转写热词已更新");
    } catch (error) {
      console.log("热词更新失败:", error);

      // 显示带链接的提示
      hotWordsUpdateFailedToastId && toast.dismiss(hotWordsUpdateFailedToastId);
      hotWordsUpdateFailedToastId = toast(<HotWordsUpdateFailedContent />, {
        autoClose: false,
        icon: <IcWarning className="w-4 h-4" />,
      });
    }
  };

  return (
    <div className={`h-full flex-col relative w-90 mr-4 bg-gradient-to-b from-#FFF5 to-#FFF3 rounded-2 border-1px border-solid border-#FFF ${visible ? 'flex' : 'hidden'}`}>
      <div className="flex justify-between items-center h-16 p-6">
        <div className="font-600 text-mx-secondary text-4.5 lh-6">发言稿</div>
        <div className="flex-1 flex justify-end items-center">
          {uploadedFile && <Popover
            placement="bottomRight"
            visible={showMoreList}
            setVisible={setShowMoreList}
            content={<div className="w-44 rd-2 text-3 text-mx-secondary">
              <div className="cursor-pointer flex justify-between items-center h-8 border-b-1px border-b-solid border-#000/10 px-2 pb-2" onClick={handleShowTransEnhanceModal}>
                <div>转写增强</div>
                <IC_RIGHT_ARROW className="size-3" />
              </div>
              <div className="cursor-pointer pl-2 mt-2 flex items-center h-8" onClick={handleReUpload}>重新上传</div>
              <div className="cursor-pointer pl-2 flex items-center h-8" onClick={() => handleFileRemove(true)}>删除</div>
            </div>}
          >
            <div className="m-l-4 size-8 cursor-pointer flex items-center justify-center relative">
              <IC_MORE className="size-4.5" />
            </div>
          </Popover>}
          {showDraftView && <IC_FOLD className="size-3.5 ml-3 cursor-pointer" onClick={onToggleSpeechDraft} />}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto mb-2">
        {!loading && <FileUploader
          visible={!uploadedFile}
          ref={fileUploaderRef}
          onFileUpload={handleFileUpload}
          isUploading={isUploading}
        />}
        {uploadedFile && <FilePreview file={uploadedFile} />}
      </div>
      <AutoTransEnhanceTips
        streamName={streamName}
        handleShowTransEnhanceModal={handleShowTransEnhanceModal}
        ref={autoTransEnhanceTipsRef}
        handleCover={handleCover} />
      <TransEnhanceModal
        ref={transEnhanceModalRef}
        streamName={streamName}
        speechDraftUrl={speechDraftUrl}
      />
    </div>
  );
};

export default SpeechDraftView;
